<template>
  <v-sheet
    outlined
    class="pa-0 pb-5 h-100"
    style="border-top: 0px; margin-left: -1px"
  >
    <div class="text-overline text-secondary pt-3 pl-5">
      {{ title }}
    </div>
    <div class="d-flex align-center pl-5 pr-3">
      <v-icon
        v-if="trend === 'up' || trend === 'down'"
        :color="trendColor(trend)"
        class="mr-1 flex-shrink-0"
      >
        {{ trend === 'up' ? 'mdi-arrow-up-thick' : 'mdi-arrow-down-thick' }}
      </v-icon>
      <span class="text-h4">{{ count }}</span>
      <span
        v-if="trendPercent"
        :class="['text-subtitle-1 font-weight-medium ml-2', `${trendColor(trend)}--text`]"
      >{{ trendPercent }}</span>
    </div>
    <div
      v-for="(x, i) in extraCounts"
      :key="`extra-${i}`"
      class="d-flex align-center pl-5 pr-3"
    >
      <v-icon
        v-if="x.trend === 'up' || x.trend === 'down'"
        small
        :color="trendColor(x.trend)"
        class="mr-1 flex-shrink-0"
      >
        {{ x.trend === 'up' ? 'mdi-arrow-up-thick' : 'mdi-arrow-down-thick' }}
      </v-icon>
      <span class="text-h6 font-weight-regular">{{ x.text }}</span>
      <span
        v-if="x.percent"
        :class="['text-body-2 font-weight-medium ml-2', `${trendColor(x.trend)}--text`]"
      >{{ x.percent }}</span>
    </div>

    <v-expand-transition leave-absolute>
      <div v-show="showDetails">
        <v-divider class="mt-5 mb-5 mx-5" />
        <v-simple-table
          v-if="listValues.length > 0"
          class="mx-5"
          dense
        >
          <thead>
            <tr>
              <td
                v-for="(t, i) in listTitles"
                :key="i"
                :style="i === 0 ? 'min-width: 55%': ''"
                :class="['font-weight-medium', { 'px-2 text-center': i > 0 }]"
              >
                {{ t }}
              </td>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(v, i) in listValues"
              :key="i"
            >
              <td>
                <v-icon
                  v-if="v.color"
                  x-small
                  :color="v.color"
                  style="padding-bottom: 1px"
                >
                  mdi-circle
                </v-icon> {{ v.text }}
              </td>
              <td class="font-weight-medium text-center">
                {{ v.guests }}
              </td>
              <td class="font-weight-medium text-center">
                {{ v.reservations }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <div
          v-else
          class="text-center"
        >
          <span class="text--secondary subtitle-1">{{ noValuesLabel }}</span>
        </div>
      </div>
    </v-expand-transition>
  </v-sheet>
</template>

<script setup lang="ts">
import { PropType } from 'vue';
import { TranslateResult } from 'vue-i18n';

type Trend = 'up' | 'down' | 'flat' | '';

const props = defineProps({
  title: { type: [String, Object] as PropType<string|TranslateResult>, required: true },
  count: { type: String, required: true },
  listTitles: { type: Array as PropType<string[]>, required: true },
  listValues: {
    type: Array as PropType<Array<{ text: string, guests: number, reservations: number, color?: string }>>,
    required: true,
  },
  noValuesLabel: { type: String, required: false, default: '' },
  showDetails: { type: Boolean, required: false, default: false },
  trend: { type: String as PropType<Trend>, required: false, default: '' },
  trendPercent: { type: String, required: false, default: '' },
  inverseTrend: { type: Boolean, required: false, default: false },
  extraCounts: {
    type: Array as PropType<Array<{ text: string, trend: Trend, percent?: string }>>,
    required: false,
    default: () => [],
  },
});

const trendColor = (t: Trend) => {
  if (t !== 'up' && t !== 'down') return '';
  return ((t === 'up') !== props.inverseTrend) ? 'green' : 'red';
};
</script>
